/**
 * Chrome for signed-in pages.
 *
 * ONE route table, two chromes — the nav items below are the single source and
 * both layouts render from them. A duplicated list is how a route ends up in
 * the desktop header and not the phone tab bar.
 *
 * **Desktop (≥768px)** is cloudpatterns, straight across: a three-column grid
 * with diagonally striped side bars, a 1536px container, and a sticky 64px
 * header of full-height cells divided by 1px rules. The whole cell is the
 * hover and click target, not the text inside it.
 *
 * **Mobile (<768px)** is a title bar plus a bottom tab bar, not a hamburger.
 * A drawer costs two taps to reach anything and hides the fact that there are
 * only three places to go; a Mini App opens straight into a 390px webview and
 * the tab bar is the shape people already expect there.
 *
 * The striped bars are hidden below 768px. cloudpatterns keeps them at every
 * width (narrowed to 0.75rem), but that is 6px of decoration either side of a
 * 390px screen, which is 4% of the width of a table.
 */

import type { ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import { useEffect, useState } from 'react';
import { Avatar, cn, Popover } from '@heroui/react';
import { LEVEL_ATTRIBUTE_KEY } from '@vlastiq/contracts';
import { useAuth } from '../auth/AuthProvider.tsx';
import { fetchRoster, type RosterEntry } from '../data/api.ts';
import { ActionButton } from './ActionButton.tsx';
import { useAction } from '../lib/useAction.ts';
import { Logo } from './Logo.tsx';
import { AdminIcon, CharacterIcon, HomeIcon, ScheduleIcon } from './icons.tsx';
import { haptics } from '../telegram/haptics.ts';
import { isMiniApp } from '../telegram/launch-params.ts';
import { showBackButton } from '../telegram/webapp.ts';

interface NavItem {
  to: string;
  label: string;
  /** Shorter form for the tab bar, where four cells share 390px. */
  short: string;
  icon: ReactNode;
  adminOnly?: boolean;
}

/*
  The tab glyphs.

  ⚠️ THESE WERE FOUR HAND-DRAWN SVGs AT STROKE 1.75, and the note above them
  said "four glyphs do not justify a dependency". That was true while there were
  four; by the time the row actions, the back link and the queue menu had each
  drawn their own, the app had fourteen glyphs in three stroke weights and no
  two of them from the same hand. They all come from `components/icons.tsx` now
  — one 24-unit grid, stroke 2, round caps — and they still inherit
  `currentColor`, so a cell's hover and active states reach them for free.
*/
const glyph = 'size-5 shrink-0';

const NAV: NavItem[] = [
  {
    to: '/',
    label: 'Главная',
    short: 'Главная',
    icon: <HomeIcon className={glyph} aria-hidden="true" />,
  },
  {
    to: '/character',
    label: 'Мой персонаж',
    short: 'Персонаж',
    icon: <CharacterIcon className={glyph} aria-hidden="true" />,
  },
  {
    to: '/schedule',
    label: 'Расписание',
    short: 'Расписание',
    icon: <ScheduleIcon className={glyph} aria-hidden="true" />,
  },
  {
    to: '/admin',
    label: 'Админка',
    short: 'Админ',
    adminOnly: true,
    icon: <AdminIcon className={glyph} aria-hidden="true" />,
  },
];

/** Every tab root. Used to decide whether Telegram's BackButton is shown. */
const TAB_ROOTS = new Set(NAV.map((item) => item.to));

export function AppShell({ children }: { children: ReactNode }) {
  const { user, isAdmin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Rendered from the LIVE role, exactly as before. A demoted admin loses the
  // tab on their next request, and the API would refuse them either way.
  const items = NAV.filter((item) => !item.adminOnly || isAdmin);

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    (user?.username ? `@${user.username}` : `#${user?.telegramUserId ?? '?'}`);

  /**
   * Telegram's BackButton, bound to the router.
   *
   * Without this the hardware back gesture on a sub-page closes the whole Mini
   * App, which to the member is indistinguishable from a crash. Shown on any
   * route that is not a tab root; outside Telegram every call is a no-op and
   * this costs nothing.
   */
  const isSubPage = !TAB_ROOTS.has(location.pathname);
  useEffect(() => {
    if (!isSubPage) return undefined;
    return showBackButton(() => navigate(-1));
  }, [isSubPage, navigate]);

  return (
    <div className="min-h-dvh bg-background text-foreground md:grid md:justify-center md:[grid-template-columns:1rem_minmax(0,1536px)_1rem]">
      {/* Striped side bars. Grid COLUMNS, not fixed overlays, so they scroll
          with the page and run the full document height. `border-x` on both,
          as in the source: the outer rule is what closes the container, and
          only one side each leaves the page edge open. */}
      <div className="hidden border-x border-border stripes-left md:block" aria-hidden="true" />

      <div className="flex min-h-dvh min-w-0 flex-col">
        <DesktopHeader items={items} displayName={displayName} onLogout={logout} user={user} />
        <MobileTitleBar items={items} onLogout={logout} />

        {/*
          The bottom padding is the tab bar's height plus the device inset, so
          the last row of a table is not permanently hidden under it. It is
          only spent below md, where the bar exists.
        */}
        <main className="min-w-0 flex-1 px-4 py-5 pb-[calc(3.5rem+var(--safe-bottom)+1.25rem)] md:px-6 md:py-8 md:pb-8">
          {children}
        </main>

        <MobileTabBar items={items} />
      </div>

      <div className="hidden border-x border-border stripes-right md:block" aria-hidden="true" />
    </div>
  );
}

/* ------------------------------------------------------------- desktop -- */

function DesktopHeader({
  items,
  displayName,
  onLogout,
  user,
}: {
  items: NavItem[];
  displayName: string;
  onLogout: () => Promise<void>;
  user: ReturnType<typeof useAuth>['user'];
}) {
  const { pathname } = useLocation();
  const current = items.find((item) => matches(pathname, item));
  const isTabRoot = TAB_ROOTS.has(pathname);

  return (
    <header
      className={cn(
        'sticky top-0 z-50 hidden h-16 items-stretch border-b border-border md:flex',
        // 90% background under a blur, so content scrolling beneath is
        // suggested rather than hidden.
        'bg-background/90 backdrop-blur-md',
      )}
    >
      {/*
        ⚠️ THE PAGE'S ONLY <h1> ON DESKTOP, and it is invisible on purpose.

        The tab-root pages used to open with «Главная» / «Расписание» / «Мой
        персонаж» / «Админка» — the same four words the nav cell immediately
        above already highlights (2026-08-12, owner's call). The headings are
        gone; the heading LEVEL is not, because a page with no h1 is a document
        with no name to anything reading it as a document.

        ⚠️ TAB ROOTS ONLY. A sub-page — Админка → Расход ИИ and its five
        siblings — renders its own visible h1, because the nav names the SECTION
        and not the page; adding this one there would put «Админка» and «Расход
        ИИ» on the same level and leave the reader to guess which is the page.
        (The phone title bar does exactly that, and has since Phase 3. It is not
        worth changing, but it is not worth copying either.)
      */}
      {isTabRoot && <h1 className="sr-only">{current?.label ?? 'nn'}</h1>}

      {/* The mark alone — the word "nn" sat beside it until the artwork
          arrived, and keeping both said the name twice. */}
      <NavLink to="/" className="cell border-r border-border" aria-label="Главная">
        <Logo className="size-8 rounded-xs" />
      </NavLink>

      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/'}
          className={({ isActive }) =>
            cn(
              'cell border-r border-border text-sm',
              isActive && 'bg-accent-soft text-foreground',
            )
          }
        >
          {item.label}
        </NavLink>
      ))}

      <div className="flex-1" />

      <IdentityCell displayName={displayName} onLogout={onLogout} user={user} />
    </header>
  );
}

/**
 * The identity cell: an avatar that opens everything about you.
 *
 * It was a name and a sign-out button sitting loose in one cell, which read as
 * a single control — the header's whole grammar is "one cell, one target", and
 * two targets in one cell breaks it. An avatar is one target.
 *
 * The character name and level are fetched WHEN THE POPOVER OPENS, not on
 * mount. They are wanted on one click on one page, and this component renders
 * on every page; fetching eagerly would put a request on every navigation to
 * fill a panel most sessions never see.
 */
function IdentityCell({
  displayName,
  onLogout,
  user,
}: {
  displayName: string;
  onLogout: () => Promise<void>;
  user: ReturnType<typeof useAuth>['user'];
}) {
  const [isOpen, setOpen] = useState(false);

  return (
    <Popover isOpen={isOpen} onOpenChange={setOpen}>
      {/* `render` rather than a nested <button>: Popover.Trigger IS the button,
          and wrapping one in another nests interactive elements. */}
      {/* `avatar-square` rather than HeroUI's circle: everything else in this
          header is a rectangle with the same 2px control radius, and one round
          element in a row of square ones reads as a different component. */}
      <Popover.Trigger
        aria-label="Профиль"
        className="cell cursor-pointer border-l border-border data-[pressed]:bg-accent-soft"
      >
        <Avatar size="md" className="avatar-square">
          {user?.photoUrl ? <Avatar.Image src={user.photoUrl} alt="" /> : null}
          <Avatar.Fallback>{displayName.slice(0, 1).toUpperCase()}</Avatar.Fallback>
        </Avatar>
      </Popover.Trigger>

      <Popover.Content placement="bottom end" className="w-72">
        <Popover.Dialog className="flex flex-col gap-3 p-4">
          <IdentityPanel displayName={displayName} user={user} isOpen={isOpen} />
          <SignOut onLogout={onLogout} fullWidth />
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

/**
 * Who you are, and what your character is.
 *
 * The level comes from `/api/roster` rather than `/api/me/character`, which
 * looks like the more obvious endpoint and is not: that one returns values
 * keyed by `defId`, so reading a level from it means also fetching the
 * catalogue to learn which id `level` is. The roster is already keyed by KEY
 * and already contains this member's own row.
 */
function IdentityPanel({
  displayName,
  user,
  isOpen,
}: {
  displayName: string;
  user: ReturnType<typeof useAuth>['user'];
  isOpen: boolean;
}) {
  const [mine, setMine] = useState<RosterEntry | null | undefined>(undefined);

  useEffect(() => {
    if (!isOpen || mine !== undefined || user === null) return;
    let cancelled = false;
    void fetchRoster()
      .then(({ characters }) => {
        if (cancelled) return;
        setMine(characters.find((e) => e.character.memberId === user.telegramUserId) ?? null);
      })
      // A failed lookup is not worth an error state here — the panel's job is
      // identity, and the handle above is already correct. Fall through to the
      // "no character" line.
      .catch(() => !cancelled && setMine(null));
    return () => {
      cancelled = true;
    };
  }, [isOpen, mine, user]);

  const level = mine ? mine.values[LEVEL_ATTRIBUTE_KEY] : null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <Avatar size="md">
          {user?.photoUrl ? <Avatar.Image src={user.photoUrl} alt="" /> : null}
          <Avatar.Fallback>{displayName.slice(0, 1).toUpperCase()}</Avatar.Fallback>
        </Avatar>
        <div className="min-w-0">
          <div className="truncate font-medium">{displayName}</div>
          {user?.username && (
            <div className="truncate text-xs text-muted">@{user.username}</div>
          )}
        </div>
      </div>

      <dl className="grid grid-cols-[minmax(5rem,max-content)_1fr] gap-x-3 gap-y-1.5 border-t border-separator pt-3 text-sm">
        <dt className="text-muted">Персонаж</dt>
        <dd className="min-w-0 truncate">
          {mine === undefined ? (
            <span className="text-muted">…</span>
          ) : mine ? (
            mine.character.name
          ) : (
            <Link to="/character" className="text-link underline underline-offset-2">
              создать
            </Link>
          )}
        </dd>
        <dt className="text-muted">Уровень</dt>
        {/* «—», never 0 — an empty field is not a level. Same rule as the
            character list on the home page. */}
        <dd className="tabular">
          {typeof level === 'number' || (typeof level === 'string' && level !== '') ? level : '—'}
        </dd>
      </dl>
    </div>
  );
}

/* -------------------------------------------------------------- mobile -- */

/**
 * The title bar names the current tab. `--content-safe-top` keeps it clear of
 * Telegram's own chrome when the Mini App is expanded.
 *
 * It also carries the only sign-out on a phone — the tab bar has five cells
 * already and a sixth would be unreadable at 390px.
 */
function MobileTitleBar({ items, onLogout }: { items: NavItem[]; onLogout: () => Promise<void> }) {
  const { pathname } = useLocation();
  const current = items.find((item) => matches(pathname, item));

  return (
    <header
      className="sticky top-0 z-50 flex h-14 items-center gap-3 border-b border-border bg-background/90 px-4 backdrop-blur-md md:hidden"
      style={{ paddingTop: 'var(--content-safe-top)' }}
    >
      <Logo className="size-6 shrink-0" />
      <h1 className="min-w-0 flex-1 truncate text-base font-semibold">{current?.label ?? 'nn'}</h1>
      <SignOut onLogout={onLogout} />
    </header>
  );
}

/**
 * Sign out — absent inside a Mini App, on purpose.
 *
 * There is nothing there to sign out OF. The launch `initData` stays in
 * sessionStorage, so the very next request re-exchanges it and signs the
 * member straight back in; the button would clear a cookie nobody was using
 * and appear to do nothing. Identity inside Telegram is Telegram's, and the
 * way to leave is to close the app.
 *
 * On the web it is the real thing, and on a phone it is the ONLY one.
 *
 * ⚠️ IT REPORTS ITS OWN FAILURE, which it did not until 2026-08-13. `logout`
 * deliberately does not assume signed-out when the request does not land — it
 * re-derives the session instead — and re-deriving a live session renders the
 * same page back, so a failed sign-out on a shared machine looked exactly like
 * a dead button. The provider rethrows now and the runner turns it into a toast.
 */
function SignOut({
  onLogout,
  fullWidth,
}: {
  onLogout: () => Promise<void>;
  fullWidth?: boolean;
}) {
  const action = useAction();

  // AFTER the hook: a component may not call fewer hooks on some renders, and
  // `isMiniApp()` can change within a session (the launch params are captured
  // before the first paint, but this component also renders before that on a
  // cold reload).
  if (isMiniApp()) return null;

  return (
    <ActionButton
      variant="danger"
      size="sm"
      fullWidth={fullWidth}
      isPending={action.isPending('logout')}
      isDisabled={action.isBusy}
      onPress={() => void action.run('logout', onLogout, { error: 'Не удалось выйти.' })}
    >
      Выйти
    </ActionButton>
  );
}

function MobileTabBar({ items }: { items: NavItem[] }) {
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-50 flex items-stretch border-t border-border bg-background/95 backdrop-blur-md md:hidden"
      style={{ paddingBottom: 'var(--safe-bottom)' }}
      aria-label="Основная навигация"
    >
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/'}
          onClick={() => haptics.selection()}
          className={({ isActive }) =>
            cn(
              'flex h-14 flex-1 flex-col items-center justify-center gap-0.5 text-[0.6875rem] transition-colors',
              isActive ? 'text-accent' : 'text-muted',
            )
          }
        >
          {item.icon}
          <span className="max-w-full truncate px-0.5">{item.short}</span>
        </NavLink>
      ))}
    </nav>
  );
}

/** Tab-root match, including sub-pages: /admin/schedule highlights Админ. */
function matches(pathname: string, item: NavItem): boolean {
  return item.to === '/' ? pathname === '/' : pathname.startsWith(item.to);
}
